/**
 * The account status badge — ONE pill, four values (Active / Free / Expired / Unknown), replacing the
 * seven-way plan_state badge and its essay tooltips. See lib/accountStatus.ts for the mapping and the
 * why. Behaviour lives there; this file only chooses a tone and renders.
 *
 * TONES. `expired` is the only one that carries urgency (danger). `active` is the healthy default (ok).
 * `free` and `unknown` are both quiet, factual states — neutral, never an alarm; the WORD carries the
 * meaning, not the colour. StatusPill renders a dot AND text, so colour is never the only signal.
 *
 * COMPACT. In dense rows (search results, tables) "Active" is the norm and needs no badge — only the
 * three exceptions (Free / Expired / Unknown) earn one, so a scannable list is not a wall of green pills.
 * On the Customer-360 header (compact=false) all four render, including Active, so the account's state
 * is always stated outright on the page a human opened on purpose.
 *
 * FAIL-SAFE. accountStatusOf normalises an absent / null / unrecognised plan_state to `unknown`, which
 * (with no base_type) reads as `free` — it never accuses a live customer of being expired.
 *
 * ⚠️ 2026-09-18 (API 0030): `plan_badge` is a fifth, OPTIONAL prop carrying the server's own ruling-based
 * verdict. Present → it wins outright over plan_state/base_type (see lib/accountStatus.ts); absent (an
 * old/stripped payload) → this renders exactly as it always did. Threading it through changes nothing
 * about the tones or the compact rule below — both still key off the resolved AccountStatus, whichever
 * path produced it.
 *
 * ⚠️ 2026-09-18 (round 3, ruling 3) — TWO HINTS SHARE ONE WORD. `status === "unknown"` now has two
 * distinct causes that both render the word "Unknown", and they get DIFFERENT hints:
 *   - a server-sent `plan_badge: "unknown"` (0030's own SETTLED verdict — "not Free AND zero renderable
 *     lines") shows the NEW hint, `accountStatus.noPlanDataHint` ("No plan data for this account yet");
 *   - the FALLBACK's own `unknown` (an opaque plan code the pre-0030 collapse could not classify — e.g. a
 *     `PLANCODE_1127` this build has never mapped) keeps the OLD hint, `accountStatus.unknownHint`.
 * Checked directly against the raw `plan_badge` prop, not re-derived: `plan_badge === "unknown"` can only
 * be true when the server actually sent that literal, because accountStatusOf's whitelist would have
 * returned some OTHER value otherwise (never producing "unknown" as a side effect of the fallback while
 * plan_badge itself reads something else).
 */

import { useTranslation } from "react-i18next";
import { StatusPill, type StatusTone } from "@/components/ui";
import { accountStatusOf, type AccountStatus } from "@/lib/accountStatus";
import type { PlanBadge, PlanState } from "@/api/types";

const TONE: Record<AccountStatus, StatusTone> = {
  active: "ok",
  free: "neutral",
  expired: "danger",
  unknown: "neutral",
};

export interface AccountStatusBadgeProps {
  /** Absent / null / unrecognised → normalises to unknown → renders "Free" (no base_type) or "Unknown". */
  plan_state?: PlanState | null | undefined;
  /** The real plan SKU. Present but unclassifiable → "Unknown"; absent → "Free". */
  base_type?: string | null | undefined;
  /** The 0030 ruling's verdict. Present → wins outright; absent/null → falls back to the two props above. */
  plan_badge?: PlanBadge | null | undefined;
  /** Dense rows: hide the default "Active" — only Free / Expired / Unknown are surfaced. */
  compact?: boolean | undefined;
  className?: string | undefined;
}

export function AccountStatusBadge({
  plan_state,
  base_type,
  plan_badge,
  compact = false,
  className,
}: AccountStatusBadgeProps) {
  const { t } = useTranslation();
  const status = accountStatusOf(plan_state, base_type, plan_badge);

  // "Active" is the norm in a dense list; only the exceptions need a badge.
  if (compact && status === "active") return null;

  // "Unknown" is the one label the principal found unclear on its own — a short hover hint, nothing more.
  // Two DIFFERENT causes share the one word (round 3, ruling 3) — see the doc block above.
  const title =
    status === "unknown"
      ? plan_badge === "unknown"
        ? t("accountStatus.noPlanDataHint")
        : t("accountStatus.unknownHint")
      : undefined;

  return (
    <StatusPill tone={TONE[status]} {...(className ? { className } : {})}>
      <span {...(title ? { title } : {})}>{t(`accountStatus.${status}`)}</span>
    </StatusPill>
  );
}
